"use client";

import { useCallback, useEffect, useSyncExternalStore } from "react";

/**
 * Dark/light toggle. ThemeScript (inline, before paint) resolves the
 * initial theme; this component owns only the interactive switch and
 * persistence. `useSyncExternalStore` reads the live theme class, so
 * no setState-in-effect and no hydration mismatch.
 */

let listeners: Array<() => void> = [];

function subscribe(listener: () => void) {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

function getSnapshot(): boolean {
  return document.documentElement.classList.contains("light");
}

function getServerSnapshot(): boolean {
  return false;
}

function emit() {
  for (const l of listeners) l();
}

export default function ThemeToggle() {
  const light = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const toggle = useCallback(() => {
    const next = !document.documentElement.classList.contains("light");
    document.documentElement.classList.toggle("light", next);
    try {
      localStorage.setItem("theme", next ? "light" : "dark");
    } catch {
      /* storage unavailable — theme applies for this visit only */
    }
    emit();
  }, []);

  // Keep the icon correct even if the theme is changed elsewhere.
  useEffect(() => {
    const obs = new MutationObserver(() => emit());
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => obs.disconnect();
  }, []);

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={light ? "Switch to dark theme" : "Switch to light theme"}
      aria-pressed={light}
      className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-line text-muted transition-colors hover:border-line-strong hover:text-ink"
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 16 16"
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
      >
        {light ? (
          <path
            d="M13.5 9.5A5.5 5.5 0 0 1 6.5 2.5a5.5 5.5 0 1 0 7 7Z"
            strokeLinejoin="round"
          />
        ) : (
          <>
            <circle cx="8" cy="8" r="3" />
            <path
              d="M8 1.5v1.6M8 12.9v1.6M1.5 8h1.6M12.9 8h1.6M3.4 3.4l1.1 1.1M11.5 11.5l1.1 1.1M12.6 3.4l-1.1 1.1M4.5 11.5l-1.1 1.1"
              strokeLinecap="round"
            />
          </>
        )}
      </svg>
    </button>
  );
}